/**
 * Shared ZIP -> Stack entry form used by the homepage hero and bottom CTA.
 * Mirrors the handler on /stack: a valid 5-digit ZIP deep-links into
 * /grant-finder?zip=xxxxx, anything else falls through to /grant-finder so the
 * Stack run (and its instrumentation) still happens.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "@/lib/router-compat";
import { MapPin, ArrowRight } from "lucide-react";
import { trackHomeCtaClick } from "@/lib/homeCta";
import { getLastZip, setLastZip } from "@/lib/lastZip";

type Props = {
  /** Analytics section label, e.g. "hero" or "bottom_cta". */
  section: string;
  /** Adds the critical-CSS hook used by the hero's LCP path. */
  ctaAttr?: boolean;
  className?: string;
};

const StackZipForm = ({ section, ctaAttr = false, className = "" }: Props) => {
  const navigate = useNavigate();
  const [zip, setZip] = useState("");

  // Reuse a ZIP the visitor already gave us elsewhere (LP hero, Stack tool).
  useEffect(() => {
    const saved = getLastZip();
    if (saved) setZip((prev) => prev || saved);
  }, []);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = zip.trim();
    trackHomeCtaClick("Run my Stack", section);
    if (/^\d{5}$/.test(trimmed)) setLastZip(trimmed);
    navigate(/^\d{5}$/.test(trimmed) ? `/grant-finder?zip=${trimmed}` : "/grant-finder");
  };

  return (
    <form
      onSubmit={onSubmit}
      className={`w-full max-w-md rounded-2xl bg-background p-1.5 shadow-xl flex flex-col sm:flex-row gap-2 ${className}`}
    >
      <div className="relative flex-1">
        <MapPin
          className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
          aria-hidden
        />
        <input
          type="text"
          inputMode="numeric"
          pattern="\d{5}"
          maxLength={5}
          value={zip}
          onChange={(e) => setZip(e.target.value.replace(/\D/g, "").slice(0, 5))}
          placeholder="Enter your ZIP code"
          aria-label="ZIP code"
          className="w-full rounded-xl bg-transparent pl-9 pr-4 py-3 text-base text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent"
        />
      </div>
      <button
        type="submit"
        {...(ctaAttr ? { "data-hero-cta": "" } : {})}
        className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-xl bg-accent px-7 py-3 text-base font-semibold text-accent-foreground hover:bg-accent/90 transition-colors"
      >
        Run my Stack
        <ArrowRight size={16} />
      </button>
    </form>
  );
};

export default StackZipForm;
